import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';

const HinglajKuldeviPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <a href="/about" className="hover:text-white">About Mahasabha</a>
            <span>›</span>
            <span>Kuldevi Shri Hinglaj</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              कुलदेवी श्री हिंगलाज
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Kuldevi Shri Hinglaj - Our Divine Mother Goddess
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Divine Salutation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-orange-600">
            श्री हिंगुलायै नमः
          </h2>
        </motion.div>

        {/* Main Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl max-w-3xl mx-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_formbuilder-15/artifacts/6x6rjvda_undefined.jpg"
              alt="श्री हिंगलाज माता - Main Shakti Peeth"
              className="w-full h-auto"
            />
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 text-center">
              <p className="font-semibold">श्री हिंगलाज मुख्य शक्तिपीठ</p>
              <p className="text-sm text-white/80">Main Shakti Peeth - Baluchistan</p>
            </div>
          </div>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 mb-12 border border-orange-100"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">कुलदेवी श्री हिंगलाज</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                श्री हिंगलाज माता हमारी कुल देवी है। दक्ष यज्ञ के विनाशोपरांत सती का ब्रह्मरंघ्र जिस स्थान पर गिरा, वह स्थान मुख्य शक्तिपीठ बना। श्री हिंगलाज मुख्य शक्तिपीठ वर्तमान में बलुचिस्तान में है।
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                रावण वध पश्चात भगवान श्री राम ने ब्रह्महत्या निवारणार्थ श्री हिंगलाज शक्तिपीठ की यात्रा की।
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                ऋषि दधीचि ने ब्रह्मक्षत्रियों को श्री हिंगलाज को कुलदेवी मानकर माता की भक्ति करने का आदेश दिया। ऋषि ने श्री हिंगलाज मंत्र भी दिया।
              </p>
            </div>
          </div>
        </motion.div>

        {/* Temples Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gray-50 rounded-2xl p-8 mb-12"
        >
          <p className="text-gray-700 text-lg leading-relaxed">
            कालान्तर में अनेक स्थानों पर श्री हिंगलाज के विग्रह स्थापित कर मां के मंदिरों का निर्माण हुआ। ब्रह्मक्षत्रियों पर मां की विशेष कृपा है। माता हिंगलाज हमारी ऊर्जा, शक्ति व एकता का स्रोत है। हमारी आस्था में कोई कमी नहीं है। ऊर्जा व समृद्धि का प्रसाद श्री हिंगलाज हमें अनवरत प्रदान कर रही है।
          </p>
        </motion.div>

        {/* Mantra Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-red-600 to-orange-600 rounded-2xl p-8 text-white mb-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">श्री हिंगलाज मंत्र</h2>
            <p className="text-white/80">Shri Hinglaj Mantra</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center mb-8">
            <p className="text-2xl md:text-3xl font-serif leading-relaxed">
              ॐ हिंगुले परम हिंगुले, अमृत रूपिणी।
            </p>
            <p className="text-2xl md:text-3xl font-serif leading-relaxed mt-2">
              तनुशक्ति मनः शिवे, श्री हिंगुलाय नमः।।
            </p>
          </div>
          
          <div className="text-white/90 leading-relaxed">
            <p className="mb-4">
              ऋषि प्रदत्त श्री हिंगलाज मंत्र माता की भक्ति का बड़ा साधन है। मंत्र मन को नियंत्रित करने वाला उपकरण है। मनन से जो मुक्ति दिलाए वही मंत्र है।
            </p>
            <p>
              यह ध्वनि की दिव्य ऊर्जा है, जो चित्त को शुद्ध कर मां से जोड़ती है। यह जुड़ाव बना रहे व जीवन सफल होता रहे, यही प्रार्थना है।
            </p>
          </div>
        </motion.div>

        {/* Closing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center py-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-100 to-red-100 rounded-full mb-6">
            <span className="text-4xl">🙏</span>
          </div>
          <h2 className="text-3xl font-bold text-primary mb-4">जय श्री हिंगलाज</h2>
          <p className="text-gray-600 text-lg">Jai Shri Hinglaj</p>
        </motion.div>
      </div>
    </div>
  );
};

export default HinglajKuldeviPage;
